import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"
import { modalOverlayClassName } from "../../lib/overlay"
import { isReactSelectOutsideEvent } from "../../lib/react-select-portal"
const Sheet = ({
 modal = false,
 ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & { modal?: boolean }) => (
 <SheetScrollLockedRoot modal={modal} {...props} />
)
const SheetTrigger = DialogPrimitive.Trigger
const SheetClose = DialogPrimitive.Close
const SheetPortal = DialogPrimitive.Portal

function SheetScrollLockedRoot({
 modal = false,
 open,
 defaultOpen,
 onOpenChange,
 ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & { modal?: boolean }) {
 const [uncontrolledOpen, setUncontrolledOpen] = React.useState<boolean>(defaultOpen ?? false)
 const isControlled = open !== undefined
 const isOpen = isControlled ? Boolean(open) : uncontrolledOpen

 React.useEffect(() => {
 if (!isOpen) return
 const body = document.body
 const prevOverflow = body.style.overflow
 body.style.overflow ="hidden"
return () => {
 body.style.overflow = prevOverflow
 }
 }, [isOpen])

 const handleOpenChange = React.useCallback(
 (next: boolean) => {
 if (!isControlled) setUncontrolledOpen(next)
 onOpenChange?.(next)
 },
 [isControlled, onOpenChange]
 )

 return (
 <DialogPrimitive.Root
 modal={modal}
 open={isControlled ? open : uncontrolledOpen}
 onOpenChange={handleOpenChange}
 {...props}
 />
 )
}

const SheetOverlay = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Overlay>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Overlay
 className={cn(
modalOverlayClassName,
"z-50",
 className
 )}
 {...props}
 ref={ref}
 />
))
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName

const sheetVariants = cva(
"fixed z-50 flex min-w-0 max-w-[100vw] flex-col overflow-hidden bg-background p-4 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 sm:p-6",
 {
 variants: {
 side: {
 top:"inset-x-0 top-0 border-b max-h-dvh data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
 bottom:
"inset-x-0 bottom-0 border-t max-h-dvh data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
 left:"inset-y-0 left-0 h-dvh w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:w-3/4 sm:max-w-sm",
 right:
"inset-y-0 right-0 h-dvh w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:w-3/4 sm:max-w-sm",
 },
 },
 defaultVariants: {
 side:"right",
 },
 }
)

interface SheetContentProps
 extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
 VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Content>,
 SheetContentProps
>(({ side ="right", className, children, onPointerDownOutside, onInteractOutside, onFocusOutside, ...props }, ref) => (
 <SheetPortal>
 <SheetOverlay />
 <DialogPrimitive.Content
 ref={ref}
 data-responsive-sheet="true"
 className={cn(sheetVariants({ side }), className)}
 onPointerDownOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onPointerDownOutside?.(e)
 }}
 onInteractOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onInteractOutside?.(e)
 }}
 onFocusOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onFocusOutside?.(e)
 }}
 {...props}
 >
 <div data-testid="sheet-scroll-region" data-responsive-scroll-region="true" className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
 <DialogPrimitive.Close aria-label="Close" className="absolute end-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-lg ring-offset-background transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary sm:end-6 sm:top-6">
 <X className="h-4 w-4"/>
 <span className="sr-only">Close</span>
 </DialogPrimitive.Close>
 </DialogPrimitive.Content>
 </SheetPortal>
))
SheetContent.displayName = DialogPrimitive.Content.displayName

const SheetHeader = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"sticky top-0 z-10 flex min-w-0 flex-col space-y-2 bg-background/95 pe-14 pb-1 text-center sm:pe-16 sm:text-start [&>*]:min-w-0",
 className
 )}
 {...props}
 />
)
SheetHeader.displayName ="SheetHeader"
const SheetFooter = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"sticky bottom-0 z-10 flex min-w-0 flex-col-reverse gap-2 border-t border-border-subtle bg-background/95 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:flex-row sm:justify-end sm:space-x-2 sm:space-x-reverse [&>*]:min-w-0 [&>*]:max-w-full [&>*]:w-full sm:[&>*]:w-auto",
 className
 )}
 {...props}
 />
)
SheetFooter.displayName ="SheetFooter"
const SheetTitle = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Title>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Title
 ref={ref}
 className={cn("break-words text-lg font-semibold text-foreground", className)}
 {...props}
 />
))
SheetTitle.displayName = DialogPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Description>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Description
 ref={ref}
 className={cn("text-sm text-muted-foreground", className)}
 {...props}
 />
))
SheetDescription.displayName = DialogPrimitive.Description.displayName

export {
 Sheet,
 SheetPortal,
 SheetOverlay,
 SheetTrigger,
 SheetClose,
 SheetContent,
 SheetHeader,
 SheetFooter,
 SheetTitle,
 SheetDescription,
}
